import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../assets/LED-Speed-Signs-Logo.png';
import logoWhite from '../assets/LED-Speed-Signs-Logo-White.png';
import RealismButton from './RealismButton';

const NAV_ITEMS = [
  { label: 'Home', path: '/' },
  { label: 'Features', path: '/features' },
  { label: 'Product Specifications', path: '/products' },
  { label: 'Service Plans', path: '/service-plans' },
  { label: 'Contact Us', path: '/contact-us' },
];

const PublicNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.includes(path);

  return (
    <header className="pointer-events-none fixed inset-x-0 top-0 z-50 flex flex-col items-center">
      <div
        className={`pointer-events-auto mt-1 flex items-center justify-between gap-4 rounded-full border backdrop-blur-md transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] ${
          scrolled
            ? 'w-[calc(100%-1.5rem)] max-w-[1200px] border-black/5 bg-white/50 px-4 py-1.5 shadow-[0_8px_30px_rgba(13,18,38,0.12)] md:px-6'
            : 'w-full max-w-none border-transparent bg-transparent px-6 py-2.5 shadow-none md:px-12'
        }`}
      >
        {/* Logo */}
        <button
          type="button"
          onClick={() => navigate('/')}
          className="flex shrink-0 items-center"
          aria-label="Go to home"
        >
          <img
            src={scrolled ? logo : logo}
            alt="Company Logo"
            className="w-auto transition-all duration-300"
            style={{ height: scrolled ? '38px' : '46px' }}
          />
        </button>

        {/* Nav links */}
        <nav className="hidden items-center gap-7 md:flex">
          {NAV_ITEMS.map(({ label, path }) => {
            const active = isActive(path);
            return (
              <button
                key={label}
                type="button"
                onClick={() => navigate(path)}
                className={`group relative py-1 text-[15px] font-semibold transition-colors ${
                  active ? 'text-black' : 'text-black/50 hover:text-black/90'
                }`}
              >
                {label}
                <span
                  className={`absolute -bottom-0.5 left-1/2 h-0.5 -translate-x-1/2 rounded-full bg-[linear-gradient(90deg,#ff3b30,#30d158,#0a84ff)] transition-all duration-300 ${
                    active ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                />
              </button>
            );
          })}
        </nav>

        {/* Right actions */}
        <div className="flex shrink-0 items-center gap-2 md:gap-3">
          <button
            type="button"
            onClick={() => navigate('/landing')}
            className="hidden rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 transition-all duration-300 hover:border-transparent hover:bg-[linear-gradient(135deg,#4ade80,#0a84ff)] hover:text-white sm:block"
          >
            Log In
          </button>
        </div>
      </div>

      {/* LED underline: full screen width at top; shrinks to pill width on scroll, with curved ends */}
      <div
        className={`pointer-events-none relative h-[1.5px] transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] ${
          scrolled ? 'w-[calc(100%-1.5rem)] max-w-[1155px]' : 'w-full max-w-none'
        }`}
      >
        {/* The LED line itself, with curved ends */}
        <div className="absolute inset-x-0 bottom-0 h-[1.5px] animate-led-sweep rounded-full [background-size:200%_100%] bg-[linear-gradient(90deg,transparent,rgba(255,59,48,0.2),#ff3b30,#30d158,#0a84ff,rgba(10,132,255,0.2),transparent)] shadow-[0_0_14px_rgba(190,196,206,0.55)] motion-reduce:animate-none" />
      </div>
    </header>
  );
};

export default PublicNavbar;
